import React from "react";
import { FileText, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddConditionButton } from "./ConditionsModal";
import { Condition } from "@/lib/dashboard/conditionsData";
import { useMedicalConditionsState } from "@/lib/dashboard/conditionsData";
import { Skeleton } from "@mui/material";

interface PatientConditionsProps {
  isDarkMode: boolean;
  onSaveCondition: (conditionData: any) => void;
  onEditCondition: (index: number, conditionData: any) => void;
  onDeleteCondition: (id: number) => void;
  hasConsultationPermission?: boolean;
}


export function PatientConditions({
  onSaveCondition,
  onEditCondition,
  onDeleteCondition,
  hasConsultationPermission = false,
}: PatientConditionsProps) {
  const {
    darkMode: isDarkMode,
    DashboardPatientConditions,
    patientCondtionRelatedLoading,
  } = useMedicalConditionsState();
  const conditions = DashboardPatientConditions;
  const canModify = hasConsultationPermission;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-sm font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Medical Conditions
        </h3>
        {canModify && <AddConditionButton onSave={onSaveCondition} />}
      </div>

      <div className="space-y-2">
        {patientCondtionRelatedLoading ? (
          // ✅ Skeletons when loading
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 border rounded-xl ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <Skeleton
                variant="circular"
                width={8}
                height={8}
                sx={{ bgcolor: isDarkMode ? "grey.800" : "grey.200" }}
              />
              <div className="flex-1">
                <Skeleton
                  variant="rounded"
                  height={14}
                  width="60%"
                  sx={{
                    bgcolor: isDarkMode ? "grey.800" : "grey.200",
                    borderRadius: "6px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={12}
                  width="40%"
                  sx={{
                    bgcolor: isDarkMode ? "grey.800" : "grey.200",
                    borderRadius: "6px",
                    mt: 0.5,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={12}
                  width="30%"
                  sx={{
                    bgcolor: isDarkMode ? "grey.800" : "grey.200",
                    borderRadius: "6px",
                    mt: 0.5,
                  }}
                />
              </div>
              <Skeleton
                variant="rounded"
                height={20}
                width={60}
                sx={{
                  bgcolor: isDarkMode ? "grey.800" : "grey.200",
                  borderRadius: "10px",
                }}
              />
            </div>
          ))
        ) : conditions.length === 0 ? (
          <div
            className={`text-center py-6 border rounded-xl transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No medical conditions recorded</p>
            <p className="mt-1 text-xs opacity-75">
              Click the + button to add conditions
            </p>
          </div>
        ) : (
          conditions.map((condition, index) => (
            <div
              key={condition.id}
              className={`flex items-center gap-3 p-3 border rounded-xl group transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <div
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {condition.name}
                </div>
                {condition.notes && (
                  <div
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {condition.notes}
                  </div>
                )}
                {condition.diagnosedDate && (
                  <div
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Diagnosed:{" "}
                    {new Date(condition.diagnosedDate).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div
                className={`text-xs px-2 py-1 rounded-full capitalize ${
                  condition.status === "active"
                    ? isDarkMode
                      ? "bg-green-900/30 text-green-300 border border-green-700"
                      : "bg-green-100 text-green-700"
                    : condition.status === "chronic"
                    ? isDarkMode
                      ? "bg-orange-900/30 text-orange-300 border border-orange-700"
                      : "bg-orange-100 text-orange-700"
                    : isDarkMode
                    ? "bg-gray-800 text-gray-300 border border-gray-600"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {condition.status}
              </div>
              {canModify && (
                <div className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                  <AddConditionButton
                    editMode={{
                      id: condition.id,
                      conditionName: condition.name,
                      status: condition.status as
                        | "active"
                        | "resolved"
                        | "chronic",
                      diagnosedDate: condition.diagnosedDate,
                      notes: condition.notes,
                    }}
                    onSave={(data) => onEditCondition(index, data)}
                    trigger={
                      <Button variant="ghost" size="sm" className="w-6 h-6 p-0">
                        <Edit2 className="w-3 h-3 text-gray-500" />
                      </Button>
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-6 h-6 p-0"
                    onClick={() => onDeleteCondition(index)}
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
